import React from 'react';
import { ButtonBase, Tooltip } from '@mui/material';
import type { ButtonBaseProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { ASSISTANT_ICON_URL, ASSISTANT_NAME } from './assistantIdentity';
import { EDGE_INSET, LAUNCHER_BOTTOM, LAUNCHER_SIZE } from './assistantLayout';

export interface AssistantLauncherProps extends Omit<ButtonBaseProps, 'onClick'> {
  onClick: () => void;
  /** When true the launcher becomes the close control for the open panel. */
  open?: boolean;
  /**
   * Set by `EmailVerifyGuardTooltip`, which clones this element to disable it.
   * When disabled the guard supplies its own tooltip explaining why, so this
   * component suppresses its own to avoid two tooltips on one target.
   */
  disabled?: boolean;
}

/**
 * Rounded-square rather than a circular `Fab`, so the square app icon reads as
 * itself instead of being cropped. The 25% corner radius matches the icon
 * treatment in the mobile app promo banner.
 */
const LauncherButton = styled(ButtonBase)(({ theme }) => ({
  position: 'fixed',
  right: EDGE_INSET.sm,
  // Sits above Pendo's Resource Center badge, which is pinned to the same
  // corner and is positioned from the Pendo dashboard rather than from here.
  bottom: LAUNCHER_BOTTOM,
  // Above page content but below the drawer it opens, which is at
  // theme.zIndex.drawer (1200).
  zIndex: theme.zIndex.speedDial,
  width: LAUNCHER_SIZE,
  height: LAUNCHER_SIZE,
  borderRadius: LAUNCHER_SIZE * 0.25,
  overflow: 'hidden',
  boxShadow: '0 4px 14px rgba(16, 24, 40, 0.28)',
  transition: theme.transitions.create(['transform', 'box-shadow'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 20px rgba(16, 24, 40, 0.34)',
  },
  '&.Mui-focusVisible': {
    outline: `3px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
  '&.Mui-disabled': {
    opacity: 0.5,
  },
  // Respect a reduced-motion preference rather than animating regardless.
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '&:hover': { transform: 'none' },
  },
}));

const LauncherIcon = styled('img')({
  width: '100%',
  height: '100%',
  display: 'block',
  objectFit: 'cover',
});

/**
 * Fixed bottom-right entry point to the assistant, styled as the Gate Check
 * app icon so the two surfaces read as the same product.
 */
const AssistantLauncher = ({
  onClick,
  open = false,
  disabled = false,
  ...rest
}: AssistantLauncherProps) => {
  const label = open ? `Close ${ASSISTANT_NAME}` : `Open ${ASSISTANT_NAME}, the SecurSpace assistant`;

  const button = (
    <LauncherButton
      {...rest}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-expanded={open}
      // Circular while it is a close control, so it reads as an action rather
      // than as the app it opens.
      sx={
        open
          ? (theme) => ({
              borderRadius: '50%',
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': { backgroundColor: theme.palette.primary.dark },
            })
          : undefined
      }
    >
      {open ? (
        <CloseIcon />
      ) : (
        // Decorative: the button's aria-label already names the assistant.
        <LauncherIcon src={ASSISTANT_ICON_URL} alt="" />
      )}
    </LauncherButton>
  );

  if (disabled) return button;

  return (
    <Tooltip title={open ? `Close ${ASSISTANT_NAME}` : `Ask ${ASSISTANT_NAME}`} placement="left" arrow>
      {button}
    </Tooltip>
  );
};

export default AssistantLauncher;
